/**
 * Virtualized Table — ui-ux-pro-max + MASTER Density 8/10
 * High-performance virtualization; dense rows; sticky headers;
 * empty/error with focusable next actions; tabular nums.
 */

'use client';

import React, { useState, useMemo, useCallback, memo, useRef } from 'react';
import { Table } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { InboxOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { FeedbackState } from '@/shared/components/feedback/FeedbackState';
import styles from './tables.module.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface VirtualizedTableProps<T = any> {
  /** Data array */
  data: T[];
  /** Table columns configuration */
  columns: ColumnsType<T>;
  /** Row height in pixels — dense default 36 (Density 8/10) */
  rowHeight?: number;
  /** Container height in pixels */
  height?: number;
  /** Loading state */
  loading?: boolean;
  /** Row key function */
  rowKey?: string | ((record: T, index?: number) => string);
  /** Row selection */
  rowSelection?: TableProps<T>['rowSelection'];
  /** Pagination settings */
  pagination?: TableProps<T>['pagination'];
  /** Scroll settings */
  scroll?: TableProps<T>['scroll'];
  /** Custom CSS classes */
  className?: string;
  /** Row click handler */
  onRow?: TableProps<T>['onRow'];
  /** Custom row renderer */
  components?: TableProps<T>['components'];
  /** Refresh for empty/error next action */
  onRefresh?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Error message — empty error row with retry */
  errorMessage?: string;
  /** Sticky column headers (default true) */
  stickyHeaders?: boolean;
  /** Apply tabular-nums (default true) */
  tabularNums?: boolean;
}

function VirtualizedTableInner<T extends Record<string, unknown>>({
  data,
  columns,
  rowHeight = 36,
  height = 400,
  loading = false,
  rowKey = 'id',
  rowSelection,
  pagination,
  scroll,
  className,
  onRow,
  components,
  onRefresh,
  emptyTitle = 'No rows',
  emptyDescription = 'There is nothing to show in this list yet.',
  errorMessage,
  stickyHeaders = true,
  tabularNums = true,
}: VirtualizedTableProps<T>) {
  const tableRef = useRef<HTMLDivElement>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const sourceData = useMemo(() => (errorMessage ? [] : data), [errorMessage, data]);

  const rowVirtualizer = useVirtualizer({
    count: sourceData.length,
    getScrollElement: () => tableRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalHeight = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalHeight - (virtualRows[virtualRows.length - 1]?.end ?? 0)
      : 0;

  const virtualData = useMemo(() => {
    return virtualRows
      .map((virtualRow) => sourceData[virtualRow.index])
      .filter((row): row is T => row !== undefined);
  }, [virtualRows, sourceData]);

  const handleRowSelection = useCallback(
    (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
      if (rowSelection?.onChange) {
        rowSelection.onChange(selectedKeys, [], { type: 'all' });
      }
    },
    [rowSelection]
  );

  const virtualComponents = useMemo(
    () => ({
      ...components,
      body: {
        ...components?.body,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        wrapper: ({ children, ...props }: any) => (
          <div
            {...props}
            style={{
              height: `${totalHeight}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${paddingTop}px`,
              }}
              aria-hidden
            />
            <div
              style={{
                position: 'absolute',
                top: `${paddingTop}px`,
                left: 0,
                width: '100%',
                height: `${virtualRows.length * rowHeight}px`,
              }}
            >
              {children}
            </div>
            <div
              style={{
                position: 'absolute',
                top: `${paddingTop + virtualRows.length * rowHeight}px`,
                left: 0,
                width: '100%',
                height: `${paddingBottom}px`,
              }}
              aria-hidden
            />
          </div>
        ),
      },
    }),
    [components, totalHeight, paddingTop, paddingBottom, virtualRows.length, rowHeight]
  );

  const enhancedScroll = useMemo(
    () => ({
      ...scroll,
      y: height,
      x: scroll?.x || 'max-content',
    }),
    [scroll, height]
  );

  const emptyLocale = useMemo(() => {
    const body = errorMessage ? (
      <FeedbackState
        variant="error"
        compact
        icon={<WarningOutlined />}
        title="Couldn’t load data"
        description={errorMessage}
        actions={
          onRefresh
            ? [
                {
                  label: 'Retry',
                  onClick: onRefresh,
                  tone: 'primary',
                  icon: <ReloadOutlined />,
                },
              ]
            : []
        }
      />
    ) : (
      <FeedbackState
        variant="empty"
        compact
        icon={<InboxOutlined />}
        title={emptyTitle}
        description={emptyDescription}
        actions={
          onRefresh
            ? [
                {
                  label: 'Refresh',
                  onClick: onRefresh,
                  tone: 'primary',
                  icon: <ReloadOutlined />,
                },
              ]
            : []
        }
      />
    );

    return <div className={styles.emptyCell}>{body}</div>;
  }, [errorMessage, emptyTitle, emptyDescription, onRefresh]);

  const rootClass = [
    styles.virtualRoot,
    styles.table,
    stickyHeaders ? styles.stickyHeader : '',
    tabularNums ? styles.tabular : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={tableRef}
      style={
        {
          height,
          ['--table-row-height' as string]: `${rowHeight}px`,
        } as React.CSSProperties
      }
      className={rootClass}
      role="region"
      aria-label="Virtualized data table"
      aria-busy={loading || undefined}
    >
      <Table<T>
        dataSource={virtualData}
        columns={columns}
        rowKey={rowKey}
        rowSelection={
          rowSelection
            ? {
                ...rowSelection,
                selectedRowKeys,
                onChange: handleRowSelection,
              }
            : undefined
        }
        pagination={pagination ?? false}
        scroll={enhancedScroll}
        loading={loading}
        onRow={onRow}
        components={virtualComponents}
        size="small"
        bordered={false}
        sticky={stickyHeaders ? { offsetHeader: 0 } : false}
        virtual
        locale={{ emptyText: emptyLocale }}
        style={{ height: '100%' }}
      />
    </div>
  );
}

export const VirtualizedTable = memo(VirtualizedTableInner) as typeof VirtualizedTableInner & {
  displayName?: string;
};
(VirtualizedTable as { displayName?: string }).displayName = 'VirtualizedTable';

export default VirtualizedTable;
